import React, { useCallback, useEffect, useMemo } from "react";
import clsx from "clsx";
import fp from "lodash/fp";
import { Row, Col } from "react-bootstrap";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import {
	HookFormInputGroup,
	HookFormRadioButton,
	HookSelectFormGroup,
} from "components";
import { DebounceTimeout } from "globalConstants";
import { terminalCalculation, useBackendPlatform, useTerminals } from "hooks";
import {
	TerminalOwnershipTypes,
	TerminalConfigurationOption,
} from "types/enums";
import {
	alphaNumericNormalizer,
	trimNormalizer,
	float2Normalizer,
	float6Normalizer,
	trimStartSymbolNormalizer,
} from "validators";
import {
	currency,
	currencySixSigns,
	floatToSixValidation,
	floatValidation,
	maxLength100,
	maxLength50,
	maxValueEx,
	minValueEx,
	onlyDigits,
	onlyMaxDigits2,
	required,
} from "validators/validation";

import Accessories from "./accessories";
import AutoCloseSection from "./autoCloseSection";
import BillSection from "./billSection";
import ConnectionTypeSection from "./connectionTypeSection";
import IndustryTypeSection from "./industryTypeSection";
import NewRentalSection from "./newRentalSection";
import OptionsSection from "./optionsSection";
import ShippingSection from "./shippingSection";
import TerminalDevices from "./terminalDevices";
import Tokenization from "./tokenization";
import Transarmor from "./transarmor";

import styles from "../terminalSolution.module.scss";

type TerminalProps = {
	sectionName?: string;
	arraySectionName?: string;
	isLoading?: boolean;
	merchantInfo: any;
	defaultValues: { [key: string]: any } | undefined;
	backEndPlatformType: string | undefined;
	isApplication?: boolean;
	businessFormName?: string;
};

const ownerships = TerminalOwnershipTypes.getOptions();
// Ownerships without "None" option
const filteredOwnerships = ownerships?.filter(
	ownership => ownership !== TerminalOwnershipTypes?.None,
);

const Terminal = ({
	sectionName = "terminal",
	arraySectionName,
	isLoading = false,
	merchantInfo,
	defaultValues,
	backEndPlatformType,
	isApplication,
	businessFormName,
}: TerminalProps) => {
	const {
		register,
		control,
		watch,
		setValue,
		getValues,
		formState: { errors },
	} = useFormContext();
	const getFieldName = useCallback(
		(fieldName: string, isAddOnlyArraySectionName?: boolean) => {
			const formattedFieldName = fieldName ? `.${fieldName}` : "";
			const formattedArraySectionName = arraySectionName
				? `${arraySectionName}.`
				: "";
			const formattedSectionName = sectionName ? `${sectionName}` : "";

			return isAddOnlyArraySectionName
				? `${formattedArraySectionName}${fieldName}`
				: `${formattedArraySectionName}${formattedSectionName}${formattedFieldName}`;
		},
		[arraySectionName, sectionName],
	);

	const manufacturer = watch(
		getFieldName("manufacturer"),
		getValues(getFieldName("manufacturer")),
	);
	const model = watch(
		getFieldName("model"),
		getValues(getFieldName("model")),
	);

	const terminalAccessoriesValues = useWatch({
		control,
		name: getFieldName("terminalAccessories", true),
	});
	const pricePerTerminal = watch(getFieldName("pricePerTerminal"));

	const monthRentalPrice = watch(getFieldName("monthRentalPrice"));
	const equipmentInsurance = watch(getFieldName("equipmentInsurance"));
	const shippingAndHandling = watch(getFieldName("shippingAndHandling"));
	const estimatedTax = watch(getFieldName("estimatedTax"));

	const setTerminalTypeAsPurchase = () => {
		setValue(getFieldName("terminalType"), TerminalOwnershipTypes.Purchase);
	};

	const setTerminalTypeAsLease = () => {
		setValue(getFieldName("terminalType"), TerminalOwnershipTypes.Lease);
	};

	const setTerminalTypeAsRental = () => {
		setValue(getFieldName("terminalType"), TerminalOwnershipTypes.Rental);
	};

	const setTerminalTypeAsExisting = () => {
		setValue(
			getFieldName("terminalType"),
			TerminalOwnershipTypes.ExistingTerminal,
		);
	};

	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
	} = useBackendPlatform(backEndPlatformType);

	const isCloverManufacturer = useMemo(
		() => manufacturer?.name === "Clover",
		[manufacturer?.name],
	);

	const isFDManufacturer = useMemo(
		() => manufacturer?.name === "FD",
		[manufacturer?.name],
	);

	const isAmpManufacturer = useMemo(
		() => manufacturer?.name === "AMP",
		[manufacturer?.name],
	);

	const isOtherManufacturer = useMemo(
		() => manufacturer?.name === "Other",
		[manufacturer?.name],
	);

	const isOtherModel = useMemo(() => {
		return model?.name === "Other";
	}, [model?.name]);

	const isCloverFDCloverGoNFCRDRV2Model = useMemo(
		() => model?.name === "FDR Clover Go NFC RDR V2",
		[model?.name],
	);

	const isCloverStationSoloBundleWithoutCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Solo Bundle without Cash Drawer",
		[model?.name],
	);

	const isCloverStationDuoBundleWithCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Duo Bundle with Cash Drawer",
		[model?.name],
	);

	const isCloverStationDuoBundleWithoutCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Duo Bundle without Cash Drawer",
		[model?.name],
	);

	const isCloverStationSoloBundleWithCashDrawerModel = useMemo(
		() => model?.name === "Clover Station Solo Bundle with Cash Drawer",
		[model?.name],
	);

	const isCloverStationSoloTabletModel = useMemo(
		() => model?.name === "Clover Station Solo Tablet",
		[model?.name],
	);

	const isCloverStationModel = useMemo(
		() => model?.name === "Station",
		[model?.name],
	);

	const terminalConfigurationOptionValue = watch(
		getFieldName("terminalConfigurationOption"),
		defaultValues?.[sectionName]?.terminalConfigurationOption,
	);

	const isNewRental =
		terminalConfigurationOptionValue ===
		TerminalConfigurationOption.NewRental.name;

	const {
		terminalManufacturers,
		terminalModels,
		terminalAccessoryTypes,
		terminalDeviceTypes,
	} = useTerminals();

	useEffect(() => {
		if (
			isCloverManufacturer &&
			(isBackEndPlatformOmaha || isBackEndPlatformNorth)
		) {
			setValue(getFieldName("isTransarmorEnabled"), "true");
		}
	}, [
		backEndPlatformType,
		getFieldName,
		isBackEndPlatformNorth,
		isBackEndPlatformOmaha,
		isCloverManufacturer,
		sectionName,
		setValue,
	]);

	const handleChangeManufacturer = manufacturer => {
		if (model?.manufacturerId !== manufacturer?.id) {
			setValue(getFieldName("model"), null);
		}

		if (terminalAccessoriesValues?.length > 0) {
			terminalAccessoriesValues.forEach((accessories, index) => {
				setValue(
					getFieldName(`terminalAccessories.${index}.type`, true),
					null,
				);
			});
		}

		if (
			manufacturer?.name !== "Clover" &&
			(isBackEndPlatformOmaha || isBackEndPlatformNorth)
		) {
			setValue(getFieldName("isTransarmorEnabled"), "false");
		}
	};

	useEffect(() => {
		if (
			(!isBackEndPlatformOmaha &&
				!isBackEndPlatformNorth &&
				(isCloverManufacturer || isFDManufacturer)) ||
			(!isBackEndPlatformTsys && isAmpManufacturer)
		) {
			setValue(getFieldName("manufacturer"), null);
		}
	}, [
		getFieldName,
		isAmpManufacturer,
		isBackEndPlatformNorth,
		isBackEndPlatformOmaha,
		isBackEndPlatformTsys,
		isCloverManufacturer,
		isFDManufacturer,
		sectionName,
		setValue,
	]);

	const filteredModels = useMemo(() => {
		return terminalModels.filter(
			model => model?.manufacturerId === manufacturer?.id,
		);
	}, [manufacturer?.id, terminalModels]);

	const filteredTerminalManufacturers = useMemo(() => {
		let result = [...terminalManufacturers];

		if (!isBackEndPlatformOmaha && !isBackEndPlatformNorth) {
			result = fp.remove({ name: "Clover" }, result);
			result = fp.remove({ name: "FD" }, result);
		}

		if (!isBackEndPlatformTsys) {
			result = fp.remove({ name: "AMP" }, result);
		}
		return result;
	}, [
		isBackEndPlatformNorth,
		isBackEndPlatformOmaha,
		isBackEndPlatformTsys,
		terminalManufacturers,
	]);

	const { fields: terminalDeviceFields, replace } = useFieldArray({
		control,
		name: getFieldName("terminalDevices"),
		keyName: "ide",
	});

	useEffect(() => {
		const modelValue = model || defaultValues?.[sectionName]?.model;
		if (
			modelValue &&
			isLoading === false &&
			terminalDeviceFields.length === 0
		) {
			handleChangeModel(model);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	// Auto select model if there is only one model
	useEffect(() => {
		if (filteredModels.length === 1) {
			setValue(getFieldName("model"), filteredModels[0]);
		}
	}, [manufacturer?.name, filteredModels, getFieldName, setValue]);

	const onChangeHandleChangeModel = modelOption => {
		handleChangeModel(modelOption);
	};

	const handleChangeModel = modelOption => {
		let filtredDeviceTypes = terminalDeviceTypes?.filter(device =>
			device.models.includes(modelOption?.id),
		);
		if (filtredDeviceTypes?.length === 0) {
			filtredDeviceTypes = terminalDeviceTypes.filter(
				device => device?.name === "Other",
			);
		}

		if (terminalAccessoriesValues?.length > 0) {
			terminalAccessoriesValues?.forEach((accessories, index) => {
				setValue(
					getFieldName(`terminalAccessories.${index}.type`, true),
					null,
				);
			});
		}

		if (
			Array.isArray(filtredDeviceTypes) &&
			filtredDeviceTypes?.length > 0
		) {
			replace(
				filtredDeviceTypes?.map(device => {
					const defaultDevice = defaultValues?.[
						sectionName
					]?.terminalDevices.find(x => x.name === device.name);

					return {
						name: device?.name,
						deviceId: null,
						typeId: device?.id,
						id: device.id,
						serialNumber: defaultDevice?.serialNumber || "",
					};
				}),
			);
		}
	};

	useEffect(() => {
		if (!isApplication) {
			setValue(
				`${getFieldName(
					"paymentMethods",
					true,
				)}.0.billToInfo.amountToBeBilled`,
				terminalCalculation({
					terminal: {
						pricePerTerminal,
						monthRentalPrice,
						equipmentInsurance,
						shippingAndHandling,
						estimatedTax,
						terminalConfigurationOption:
							terminalConfigurationOptionValue,
					},
					terminalAccessories: terminalAccessoriesValues,
				}).toFixed(6),
			);
		}
	}, [
		equipmentInsurance,
		estimatedTax,
		getFieldName,
		isApplication,
		monthRentalPrice,
		pricePerTerminal,
		setValue,
		shippingAndHandling,
		terminalAccessoriesValues,
		terminalConfigurationOptionValue,
	]);

	const filteredAccessoryTypes = useMemo(
		() =>
			terminalAccessoryTypes.filter(
				a => a.models.includes(model?.id) || a.name === "Other",
			),
		[model?.id, terminalAccessoryTypes],
	);

	return (
		<div className={styles.section} data-autotest="terminalSection">
			<Row className="form-group">
				<Col md={2}>
					<HookFormRadioButton
						id={getFieldName("NewPurchase")}
						name={getFieldName("terminalConfigurationOption")}
						register={register}
						value={TerminalConfigurationOption.NewPurchase.name}
						defaultCheckedByValue={
							defaultValues?.[sectionName]
								?.terminalConfigurationOption
						}
						label={
							TerminalConfigurationOption.NewPurchase.displayName
						}
						blurLoading={isLoading}
						onChange={setTerminalTypeAsPurchase}
					/>
				</Col>
				<Col md={2}>
					<HookFormRadioButton
						id={getFieldName("NewLease")}
						name={getFieldName("terminalConfigurationOption")}
						register={register}
						value={TerminalConfigurationOption.NewLease.name}
						defaultCheckedByValue={
							defaultValues?.[sectionName]
								?.terminalConfigurationOption
						}
						label={TerminalConfigurationOption.NewLease.displayName}
						blurLoading={isLoading}
						onChange={setTerminalTypeAsLease}
					/>
				</Col>
				<Col md={2}>
					<HookFormRadioButton
						id={getFieldName("FileBuildOnly")}
						name={getFieldName("terminalConfigurationOption")}
						register={register}
						value={TerminalConfigurationOption.FileBuildOnly.name}
						defaultCheckedByValue={
							defaultValues?.[sectionName]
								?.terminalConfigurationOption
						}
						label={
							TerminalConfigurationOption.FileBuildOnly
								.displayName
						}
						blurLoading={isLoading}
						onChange={setTerminalTypeAsExisting}
					/>
				</Col>
				<Col md={2}>
					<HookFormRadioButton
						id={getFieldName("NewRental")}
						name={getFieldName("terminalConfigurationOption")}
						register={register}
						value={TerminalConfigurationOption.NewRental.name}
						defaultCheckedByValue={
							defaultValues?.[sectionName]
								?.terminalConfigurationOption
						}
						label={
							TerminalConfigurationOption.NewRental.displayName
						}
						blurLoading={isLoading}
						onChange={setTerminalTypeAsRental}
					/>
				</Col>
			</Row>
			<Row className="form-group">
				<Col md={5}>
					<HookSelectFormGroup
						label="Ownership"
						options={filteredOwnerships}
						getOptionLabel={option => option.displayName}
						getOptionValue={option => option.value}
						placeholder="Please Select"
						name={getFieldName("terminalType")}
						isSearchable={false}
						cacheOptions
						defaultOptions
						defaultValue={
							defaultValues?.[sectionName]?.terminalType
						}
						debounceTimeout={DebounceTimeout}
						blurLoading={isLoading}
						showRequiredMark
						rules={{
							validate: {
								required,
							},
						}}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}></Col>
			</Row>
			<Row className="form-group">
				<Col md={5}>
					<HookSelectFormGroup
						label="Manufacturer"
						options={filteredTerminalManufacturers}
						getOptionLabel={option => option.name}
						getOptionValue={option => option.id}
						placeholder="Please Select"
						name={getFieldName("manufacturer")}
						isSearchable={false}
						cacheOptions
						defaultOptions
						defaultValue={
							defaultValues?.[sectionName]?.manufacturer
						}
						debounceTimeout={DebounceTimeout}
						blurLoading={isLoading}
						showRequiredMark
						rules={{
							validate: {
								required,
							},
						}}
						onChange={handleChangeManufacturer}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					<HookSelectFormGroup
						label="Model"
						options={filteredModels}
						getOptionLabel={option => option.name}
						getOptionValue={option => option.id}
						placeholder="Please Select"
						name={getFieldName("model")}
						isSearchable={false}
						cacheOptions
						defaultOptions
						defaultValue={defaultValues?.[sectionName]?.model}
						debounceTimeout={DebounceTimeout}
						blurLoading={isLoading}
						showRequiredMark
						rules={{
							validate: {
								required,
							},
						}}
						isDisabled={
							!manufacturer?.id &&
							!defaultValues?.[sectionName]?.manufacturer?.id
						}
						onChange={onChangeHandleChangeModel}
					/>
				</Col>
			</Row>
			{(isOtherManufacturer || isOtherModel) && (
				<Row className="form-group">
					<Col md={5}>
						{isOtherManufacturer && (
							<HookFormInputGroup
								name={getFieldName("otherManufacturerName")}
								register={register}
								label="Manufacturer Name"
								defaultValue={
									defaultValues?.[sectionName]
										?.otherManufacturerName
								}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								validator={{ required, maxLength100 }}
								normalize={alphaNumericNormalizer}
								normalizeOnBlur={trimNormalizer}
								errors={errors}
								showRequiredMark
							/>
						)}
					</Col>

					<Col md={{ span: 5, offset: 2 }}>
						{isOtherModel && (
							<HookFormInputGroup
								name={getFieldName("otherModelName")}
								register={register}
								label="Model Name"
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								defaultValue={
									defaultValues?.[sectionName]?.otherModelName
								}
								autoComplete="off"
								validator={{ required, maxLength100 }}
								normalize={alphaNumericNormalizer}
								normalizeOnBlur={trimNormalizer}
								errors={errors}
								showRequiredMark
							/>
						)}
					</Col>
				</Row>
			)}
			{model && !isApplication && (
				<TerminalDevices
					isLoading={isLoading}
					terminalDeviceFields={terminalDeviceFields}
					sectionName={getFieldName("")}
				/>
			)}
			{isNewRental && (
				<>
					{isApplication ? (
						<NewRentalSection
							sectionName={getFieldName("")}
							isLoading={isLoading}
							defaultValues={defaultValues?.[sectionName]}
							placeholder={"0.000000"}
							validator={{ floatToSixValidation, required }}
							normalize={currencySixSigns}
							normalizeOnBlur={float6Normalizer}
							errors={errors}
						/>
					) : (
						<NewRentalSection
							sectionName={getFieldName("")}
							isLoading={isLoading}
							defaultValues={defaultValues?.[sectionName]}
							placeholder={"0.00"}
							validator={{ floatValidation, required }}
							normalize={currency}
							normalizeOnBlur={float2Normalizer}
							errors={errors}
						/>
					)}
				</>
			)}
			<Row className="form-group">
				{!isNewRental && (
					<>
						<Col md={5}>
							<HookFormInputGroup
								name={getFieldName("pricePerTerminal")}
								register={register}
								label="Price Per Terminal, $"
								defaultValue={
									defaultValues?.[sectionName]
										?.pricePerTerminal
								}
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								placeholder="0.00"
								showRequiredMark
								validator={{ floatValidation, required }}
								normalize={currency}
								normalizeOnBlur={float2Normalizer}
								errors={errors}
							/>
						</Col>
					</>
				)}

				{isApplication && (
					<Col md={{ span: 5, offset: !isNewRental ? 2 : 0 }}>
						<HookFormInputGroup
							name={getFieldName("quantity")}
							register={register}
							label="Quantity"
							defaultValue={
								defaultValues?.[sectionName]?.quantity
							}
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							placeholder="1"
							showRequiredMark
							validator={{
								required,
								minValueEx: minValueEx(1, ""),
								maxValueEx: maxValueEx(99, ""),
							}}
							normalize={onlyMaxDigits2}
							normalizeOnBlur={trimStartSymbolNormalizer("0")}
							errors={errors}
						/>
					</Col>
				)}
			</Row>
			{!isApplication && (
				<Row className="form-group">
					<Col md={5}>
						<HookFormInputGroup
							name={getFieldName("dvid")}
							register={register}
							label="DVID"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							validator={{ maxLength50 }}
							normalize={alphaNumericNormalizer}
							normalizeOnBlur={trimNormalizer}
							errors={errors}
						/>
					</Col>
					<Col md={{ span: 5, offset: 2 }}>
						<HookFormInputGroup
							name={getFieldName("simNumber")}
							register={register}
							label="SIM #"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							normalize={onlyDigits}
							errors={errors}
						/>
					</Col>
				</Row>
			)}
			{isCloverManufacturer && !isApplication && (
				<Row className="form-group">
					<Col md={5}>
						<HookFormInputGroup
							name={getFieldName(
								"cloverTerminalSettings.cloverId",
							)}
							register={register}
							label="Clover ID"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							validator={{ required, maxLength50 }}
							normalize={alphaNumericNormalizer}
							normalizeOnBlur={trimNormalizer}
							errors={errors}
							showRequiredMark
						/>
					</Col>
					<Col md={{ span: 5, offset: 2 }}>
						<HookFormInputGroup
							name={getFieldName(
								"cloverTerminalSettings.username",
							)}
							register={register}
							label="Clover Username"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							validator={{ maxLength50 }}
							normalizeOnBlur={trimNormalizer}
							errors={errors}
						/>
					</Col>
				</Row>
			)}
			{isCloverManufacturer && isCloverFDCloverGoNFCRDRV2Model && (
				<Row className="form-group">
					<Col md={5}>
						<HookFormInputGroup
							name={getFieldName(
								"cloverTerminalSettings.goMonthlyFee",
							)}
							register={register}
							label="Clover Go Monthly Fee, $"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							placeholder="0.000000"
							validator={{ floatToSixValidation }}
							normalize={currencySixSigns}
							normalizeOnBlur={float6Normalizer}
							readOnly
							isPlainText
							labelClassName="pr-0"
							defaultValue={
								defaultValues?.[sectionName]
									?.cloverTerminalSettings.goMonthlyFee
							}
						/>
					</Col>
					<Col md={{ span: 5, offset: 2 }}></Col>
				</Row>
			)}
			{isCloverManufacturer &&
				(isCloverStationSoloBundleWithoutCashDrawerModel ||
					isCloverStationDuoBundleWithoutCashDrawerModel ||
					isCloverStationDuoBundleWithCashDrawerModel ||
					isCloverStationSoloBundleWithCashDrawerModel ||
					isCloverStationSoloTabletModel ||
					isCloverStationModel) && (
					<Row className="form-group">
						<Col md={5}>
							<HookFormInputGroup
								name={getFieldName(
									"cloverTerminalSettings.servicesPerDeviceFee",
								)}
								register={register}
								label="Clover Services (Per Device) Fee, $"
								colInput={6}
								colLabel={6}
								blurLoading={isLoading}
								autoComplete="off"
								placeholder="0.000000"
								validator={{ floatToSixValidation }}
								normalize={currencySixSigns}
								normalizeOnBlur={float6Normalizer}
								errors={errors}
								defaultValue={
									defaultValues?.[sectionName]
										?.cloverTerminalSettings
										.servicesPerDeviceFee
								}
							/>
						</Col>
					</Row>
				)}
			{isBackEndPlatformOmaha || isBackEndPlatformNorth ? (
				<Transarmor
					sectionName={getFieldName("")}
					isLoading={isLoading}
					disabled={isCloverManufacturer}
					isCloverManufacturer={isCloverManufacturer}
					defaultValue={defaultValues?.[sectionName]}
				/>
			) : isBackEndPlatformTsys ? (
				<Tokenization
					sectionName={getFieldName("")}
					isLoading={isLoading}
					defaultValue={defaultValues?.[sectionName]}
				/>
			) : null}
			<Accessories
				sectionName={getFieldName("terminalAccessories", true)}
				isLoading={isLoading}
				terminalAccessoryTypes={filteredAccessoryTypes}
				isApplication={isApplication}
			/>
			<OptionsSection
				sectionName={getFieldName("terminalOptions")}
				isLoading={isLoading}
				defaultValue={defaultValues?.[sectionName]?.terminalOptions}
			/>
			<IndustryTypeSection
				sectionName={getFieldName("")}
				isLoading={isLoading}
				defaultValue={defaultValues?.[sectionName]?.industryType}
			/>
			<ConnectionTypeSection
				sectionName={getFieldName("")}
				isLoading={isLoading}
				connectionName="Primary"
				defaultValue={defaultValues?.[sectionName]?.connectionPrimary}
			/>
			<ConnectionTypeSection
				sectionName={getFieldName("")}
				isLoading={isLoading}
				connectionName="Backup"
				defaultValue={defaultValues?.[sectionName]?.connectionBackup}
			/>
			<AutoCloseSection
				sectionName={getFieldName("")}
				timeZoneName={merchantInfo?.timeZoneName}
				isLoading={isLoading}
				defaultValues={defaultValues?.[sectionName]}
			/>
			{!isApplication && (
				<BillSection
					sectionName={getFieldName("paymentMethods", true)}
					defaultValues={fp.cloneDeep(defaultValues?.paymentMethods)}
					isLoading={isLoading}
					merchantInfo={merchantInfo}
				/>
			)}
			{isApplication ? (
				<hr className={clsx("mt-3 mb-4")} />
			) : (
				<ShippingSection
					sectionName={getFieldName("shipping", true)}
					isLoading={isLoading}
					merchantInfo={merchantInfo}
					businessFormName={businessFormName}
				/>
			)}
		</div>
	);
};

export default Terminal;
